// ============================================================
// SpeakEasy Smart Conversation Engine v2
// 20 scenarios, keyword matching, context tracking, grammar tips
// No AI API needed — acts like AI through rich response banks
// ============================================================

// Context tracker — remembers what user talked about
let context = { topic: null, mood: null, name: null, mentioned: [], lastKeyword: null, exchangeCount: 0, topics_discussed: [] };

function updateContext(userMsg) {
  const lower = userMsg.toLowerCase();
  if (/my name is (\w+)/i.test(userMsg)) context.name = userMsg.match(/my name is (\w+)/i)[1];
  if (/happy|great|good|awesome|amazing|excited/i.test(lower)) context.mood = "positive";
  if (/sad|tired|stressed|worried|nervous|bad|awful/i.test(lower)) context.mood = "negative";
  if (/job|work|career|office|boss|colleague/i.test(lower)) context.topic = "work";
  if (/family|mother|father|brother|sister|child|kid/i.test(lower)) context.topic = "family";
  if (/travel|trip|vacation|holiday|flight|hotel/i.test(lower)) context.topic = "travel";
  if (/food|eat|cook|restaurant|dinner|lunch/i.test(lower)) context.topic = "food";
  if (/study|learn|school|university|exam|test/i.test(lower)) context.topic = "education";
  if (/health|doctor|sick|hospital|medicine|exercise/i.test(lower)) context.topic = "health";
  context.mentioned.push(lower.substring(0, 50));
}

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// Grammar tip generator — checks user message for common errors
function getGrammarTip(msg) {
  const lower = msg.toLowerCase();
  const tips = [];
  if (/\bi\b/.test(msg) && !/\bI\b/.test(msg) && !/\bin\b/.test(lower)) tips.push('💡 Tip: Always capitalize "I".');
  if (/\b(he|she|it) (go|eat|play|like|want|need|live|work|come|make|take)\b/i.test(lower)) tips.push('💡 Tip: Add -s/-es for he/she/it: "She goes", not "She go".');
  if (/\bdid\s+\w+ed\b/i.test(lower)) tips.push('💡 Tip: Use base form after "did": "Did you go?" not "Did you went?"');
  if (/\bmore\s+(better|bigger|faster|easier)\b/i.test(lower)) tips.push('💡 Tip: Don\'t use "more" with -er words. Say "better" not "more better".');
  if (/\byesterday\s+i\s+(go|eat|play|come|make|take|see|do)\b/i.test(lower)) tips.push('💡 Tip: Use past tense with "yesterday": "I went" not "I go".');
  if (/\bi\s+am\s+agree\b/i.test(lower)) tips.push('💡 Tip: Say "I agree" — no need for "am".');
  if (/\bdiscuss\s+about\b/i.test(lower)) tips.push('💡 Tip: "Discuss" doesn\'t need "about". Say "discuss the topic".');
  if (/\bmarried\s+with\b/i.test(lower)) tips.push('💡 Tip: Say "married to" not "married with".');
  if (/\bgood\s+in\b/i.test(lower)) tips.push('💡 Tip: Say "good at" not "good in".');
  if (/\blisten\s+(?!to)\w/i.test(lower)) tips.push('💡 Tip: Always say "listen TO something".');
  return tips.length > 0 ? "\n\n" + tips[0] : "";
}

// ===== UNIVERSAL RESPONSE ENGINE =====
// Handles ANY input even if no keyword matches — makes it feel like real AI

const UNIVERSAL_TOPICS = {
  "love|relationship|dating|boyfriend|girlfriend|crush|marriage|partner|heart": [
    "Love and relationships are such an important part of life! What's on your mind about this?",
    "That's a meaningful topic. Relationships take effort from both sides, don't they?",
    "It sounds like this matters a lot to you. Do you want to talk more about it?",
  ],
  "money|salary|invest|save|spend|rich|poor|budget|financial|economy|bank": [
    "Money matters are always worth discussing. What's your approach to managing finances?",
    "Financial planning is so important! Are you saving for something specific?",
    "That's a practical topic. What's your take on balancing spending and saving?",
  ],
  "technology|phone|computer|app|internet|social media|ai|robot|software|website": [
    "Technology is changing everything! What's your favorite tech topic?",
    "Interesting! Do you think technology makes life better or more complicated?",
    "Tech is fascinating. Are you into the latest gadgets and apps?",
  ],
  "politics|government|election|vote|law|policy|president|minister|democracy": [
    "That's a thought-provoking topic! What's your perspective on this?",
    "Politics affects all of us. What matters most to you about this subject?",
    "Interesting viewpoint! Do you follow politics closely?",
  ],
  "religion|god|pray|faith|church|temple|mosque|spiritual|believe|soul": [
    "Faith and spirituality are deeply personal. I respect your perspective on this.",
    "That's a meaningful topic. What role does this play in your daily life?",
    "I find these conversations very enriching. Tell me more about your beliefs.",
  ],
  "dream|goal|future|ambition|hope|wish|plan|aspire|someday|want to be": [
    "Dreams and ambitions drive us forward! What's your biggest dream right now?",
    "I love talking about the future! What's the one thing you most want to achieve?",
    "Goals give life direction. What steps are you taking toward yours?",
  ],
  "pet|dog|cat|animal|puppy|kitten|bird|fish": [
    "Pets bring so much joy! Do you have any animals?",
    "I love animals! What's your favorite kind of pet?",
    "Animals are wonderful companions. Do you have any fun pet stories?",
  ],
  "book|read|novel|story|author|library|literature|writing|poem": [
    "Books open up whole new worlds! What do you enjoy reading?",
    "A fellow reader! What's the last book that really impacted you?",
    "Reading is such a wonderful habit. What genre do you prefer?",
  ],
  "game|play|video game|chess|board game|puzzle|fun|competition": [
    "Games are a great way to relax and have fun! What do you enjoy playing?",
    "Gaming can be quite addictive! Are you competitive or casual?",
    "What's your favorite game right now? I'm always looking for recommendations!",
  ],
  "nature|tree|flower|mountain|ocean|river|sky|sunset|forest|garden": [
    "Nature is beautiful! Do you enjoy spending time outdoors?",
    "There's something magical about being in nature. What's your favorite outdoor spot?",
    "I find nature very calming. Do you like hiking, gardening, or just enjoying the view?",
  ],
  "art|painting|drawing|design|creative|color|sculpture|photography|museum": [
    "Art feeds the soul! Are you into creating or appreciating art?",
    "Creativity comes in so many forms! What's your favorite type of art?",
    "That's wonderful! Art helps us see the world differently, doesn't it?",
  ],
  "problem|difficult|challenge|hard|struggle|can't|impossible|stuck|help": [
    "I hear you. Challenges can be tough, but they also make us stronger.",
    "Don't give up! Sometimes the solution comes when we least expect it.",
    "What's the main thing that's making this difficult? Let's think it through.",
    "Every expert was once a beginner who didn't quit. You've got this!",
  ],
  "happy|excited|wonderful|celebrate|amazing|fantastic|best|love it|great news": [
    "That's absolutely wonderful! Your happiness is contagious! 🎉",
    "I'm so happy for you! You deserve to celebrate this!",
    "What amazing news! Tell me everything!",
  ],
  "sad|depressed|lonely|miss|cry|hurt|pain|lost|grief|broken": [
    "I'm sorry you're going through this. It's okay to feel this way.",
    "Tough times don't last, but tough people do. I'm here to listen.",
    "Sometimes just talking about it helps. Would you like to share more?",
    "Remember, it's okay to not be okay. Take things one day at a time.",
  ],
  "thank|grateful|appreciate|kind|helped|blessing|lucky|fortunate": [
    "You're very welcome! It makes me happy to help.",
    "Gratitude is a beautiful thing! You seem like a really thoughtful person.",
    "That's so kind of you to say! It means a lot.",
  ],
  "sorry|apologize|mistake|fault|forgive|wrong|regret|my bad": [
    "No worries at all! Everyone makes mistakes. What matters is learning from them.",
    "I appreciate you saying that. It takes courage to acknowledge mistakes.",
    "Don't be too hard on yourself. Let's focus on moving forward!",
  ],
  "time|clock|hour|minute|schedule|late|early|busy|hurry|rush": [
    "Time management is something we all struggle with! Any specific concerns?",
    "Being aware of time is a great skill. How do you usually organize your day?",
    "Life can get hectic! What's keeping you busy these days?",
  ],
};

// Detect if message is a question
function isQuestion(msg) {
  return /\?$/.test(msg.trim()) || /^(what|where|when|who|why|how|is|are|do|does|did|can|could|would|should|will|have|has|may|shall)\s/i.test(msg.trim());
}

// Universal response when nothing else matches
function getUniversalResponse(userMessage) {
  const lower = userMessage.toLowerCase();

  // Check universal topics
  for (const [pattern, responses] of Object.entries(UNIVERSAL_TOPICS)) {
    const keywords = pattern.split('|');
    if (keywords.some(kw => lower.includes(kw))) {
      return pick(responses);
    }
  }

  // If it's a question, give a thoughtful question response
  if (isQuestion(userMessage)) {
    return pick([
      "That's a really thought-provoking question! Let me think... What's your own opinion on it?",
      "Great question! I'd love to hear your thoughts first. What do you think?",
      "Hmm, interesting question! There are different ways to look at it. What's your perspective?",
      "That's the kind of question that makes you think! What prompted you to ask that?",
      "I appreciate deep questions like that! What's your take on it?",
    ]);
  }

  // If it's a statement, engage with it
  return pick([
    "That's an interesting point! I hadn't thought about it that way. Can you elaborate?",
    "I see what you mean. That's a really thoughtful perspective.",
    "You make a good point! How did you come to that conclusion?",
    "That's fascinating! I'd love to know more about your thoughts on this.",
    "Hmm, that gives me something to think about. Tell me more!",
    "I appreciate you sharing that. It shows a lot of self-awareness.",
    "That resonates with me. What else comes to mind when you think about this?",
    "You're really articulate! I enjoy conversations like this.",
  ]);
}

const SCENARIOS = {
  // ===== SCENARIO 1: JOB INTERVIEW =====
  interview: {
    emoji: "🧑‍💼", title: "Job Interview", desc: "Practice answering interview questions confidently",
    greeting: "Good morning! Thank you for coming in today. Please take a seat. Let's start — can you tell me a little about yourself?",
    keywords: {
      "experience|worked|years|company|role|position|job": [
        "That's solid experience! What specific skills did you develop in that role?",
        "Impressive! Can you walk me through a typical day in that position?",
        "Great background. What made you decide to leave that role?",
        "I see. How has that experience prepared you for this opportunity?",
        "Interesting career path! What was the biggest lesson you learned there?",
      ],
      "skill|good at|strength|best|ability|capable": [
        "Those are exactly the skills we need. Can you give me a specific example?",
        "How have you applied that skill in a real work situation?",
        "That's great. What would your previous manager say about those strengths?",
        "Impressive! Do you think there's room to develop those skills further?",
        "Perfect. How would you use those strengths in this role specifically?",
      ],
      "team|collaborate|together|group|people|coworker": [
        "Teamwork is crucial here. How do you handle disagreements within a team?",
        "Interesting! What role do you naturally take in a group — leader, organizer, or contributor?",
        "Can you describe a time when a team project didn't go as planned?",
        "How do you give constructive feedback to teammates?",
        "What's your approach when someone in the team isn't pulling their weight?",
      ],
      "challenge|difficult|problem|hard|struggle|obstacle": [
        "How did you approach solving that challenge?",
        "That sounds tough. What was the outcome?",
        "What did that experience teach you about handling pressure?",
        "Would you handle it differently if you faced the same situation again?",
        "I appreciate your honesty. Challenges build character — how did it change your approach?",
      ],
      "weakness|improve|working on|not good|struggle": [
        "Self-awareness is important. What are you doing to improve in that area?",
        "We all have weaknesses. How does that affect your work, and how do you manage it?",
        "That's a thoughtful answer. What resources are you using to improve?",
        "I appreciate your honesty. The fact that you're aware of it is already a strength.",
      ],
      "salary|pay|compensation|money|package|benefits": [
        "We offer a competitive package. What are your salary expectations?",
        "Our benefits include health insurance, 20 days leave, and learning budget. Does that interest you?",
        "That's within our range. We can discuss specifics once we move forward.",
        "Let's come back to that after we both decide if this is a good fit.",
      ],
      "question|ask|company|culture|team size|project": [
        "Great question! We're a team of 30, growing to 50 by year end. Very collaborative culture.",
        "We use agile methodology and believe in work-life balance. Any other questions?",
        "Our biggest project right now involves expanding to new markets. Exciting times!",
        "We invest heavily in employee growth — training, conferences, certifications. What interests you?",
      ],
      "learn|grow|develop|opportunity|career|future": [
        "We love that growth mindset! What skills do you want to develop next?",
        "Where do you see yourself in 3-5 years?",
        "We have a clear career progression path. That seems to align with your goals.",
        "Professional development is a priority here. What area excites you most?",
      ],
      "why|reason|leave|change|looking|new": [
        "That's understandable. What specifically are you looking for in your next role?",
        "Makes sense. What would make this position your ideal job?",
        "I appreciate your candor. What values are most important to you in a workplace?",
      ],
      "nervous|worried|first time|new to this": [
        "Don't worry, just be yourself! Take a deep breath. There are no wrong answers here.",
        "It's completely normal to feel nervous. We're just having a conversation — relax!",
        "Everyone feels that way. You're doing great so far. Let's continue!",
      ],
    },
    fallback: [
      "Interesting perspective! Can you elaborate a bit more on that?",
      "I see. How would that translate to our work environment?",
      "Thank you for sharing. Let me ask you something different — what motivates you?",
      "Good point. Can you give me a concrete example?",
      "That's helpful. What else should I know about you?",
    ],
    closing: "Thank you so much for your time today. You've given very thoughtful answers. We'll review all candidates and get back to you within a week. Do you have any final questions? Best of luck!",
  },

  // ===== SCENARIO 2: DAILY CONVERSATION =====
  daily: {
    emoji: "😊", title: "Daily Conversation", desc: "Casual everyday English with a friend",
    greeting: "Hey! Long time no see! How have you been? What's new in your life?",
    keywords: {
      "good|great|fine|well|awesome|amazing|fantastic|wonderful": [
        "That's awesome to hear! What's been making things so great?",
        "Love that energy! Anything special happening?",
        "So glad to hear! What are you most excited about right now?",
        "That's wonderful! Tell me more — I want the details!",
      ],
      "tired|busy|stressed|exhausted|overwhelmed|hectic": [
        "Oh no, you need a break! What's been keeping you so busy?",
        "I totally get that. What do you do to destress?",
        "Hang in there! Have you tried taking a short vacation?",
        "That sounds intense. Make sure you're taking care of yourself!",
        "We all have those periods. What helps you recharge?",
      ],
      "work|job|office|boss|project|meeting|colleague": [
        "How's work going? Anything interesting happening?",
        "I hear you! Work can be crazy sometimes. Any new projects?",
        "Is your boss treating you well? Office dynamics can be tricky!",
        "That sounds challenging but also like a great opportunity to learn.",
      ],
      "weekend|saturday|sunday|holiday|vacation|trip|travel": [
        "Nice! Any fun plans? I need some inspiration!",
        "That sounds perfect! Are you going anywhere special?",
        "I love weekends! What's your ideal way to spend one?",
        "Lucky you! Take lots of photos and tell me all about it!",
      ],
      "movie|show|netflix|series|watch|film|drama": [
        "Oh what are you watching? I need new recommendations!",
        "I just finished an amazing series! What genres do you like?",
        "Movie nights are the best! Do you prefer cinema or home?",
        "Is it worth watching? I'm always looking for something good!",
      ],
      "food|eat|restaurant|cook|dinner|lunch|breakfast|recipe": [
        "Mmm, that sounds delicious! What's your favorite cuisine?",
        "Are you a good cook? What's your signature dish?",
        "I love trying new restaurants! Any recommendations around here?",
        "Have you tried cooking anything new recently? I'm getting bored of my usual meals.",
      ],
      "sport|exercise|gym|run|play|football|cricket|yoga|fitness": [
        "That's great for health! How often do you work out?",
        "I should exercise more! What motivates you to stay active?",
        "Do you play any sports? Or more into gym/running?",
        "Fitness goals! Are you training for anything specific?",
      ],
      "music|song|listen|band|concert|singer|playlist": [
        "What kind of music are you into these days?",
        "Music is life! Who's your current favorite artist?",
        "Have you been to any good concerts recently?",
        "Drop me your playlist! I need new music in my life.",
      ],
      "family|parents|kids|children|mom|dad|brother|sister|married|wife|husband": [
        "How's the family doing? Everyone well?",
        "Family time is the best. Do you get to see them often?",
        "That's so sweet! Family is everything, isn't it?",
        "I love hearing about happy family moments. Tell me more!",
      ],
      "study|learn|course|class|exam|university|school|english": [
        "That's great that you're still learning! What are you studying?",
        "Education never stops. Are you enjoying it?",
        "Any exams coming up? How's the preparation going?",
        "Learning new things is so rewarding. What got you interested in that?",
      ],
      "weather|rain|sun|cold|hot|snow": [
        "Right?! The weather has been so unpredictable lately!",
        "I know! I wish it would make up its mind — rain or sunshine, pick one!",
        "Perfect weather for staying in with a cup of tea, don't you think?",
      ],
      "plan|future|dream|goal|want to|hope to|wish": [
        "That sounds like an amazing plan! How are you going to make it happen?",
        "I love that you have goals! What's the first step?",
        "Dreams are important. What's stopping you from starting right now?",
        "You should totally go for it! Life's too short to wait.",
      ],
    },
    fallback: [
      "Ha, that's so interesting! Tell me more!",
      "I know exactly what you mean! So what happened next?",
      "That's cool! What else is going on with you?",
      "I can totally relate! What are your plans for the rest of the day?",
      "Sounds like you're keeping busy! Anything exciting coming up?",
    ],
    closing: "It was SO great catching up with you! We should do this more often. Let me know when you're free next. Take care and have an amazing day! 😊",
  },

  // ===== SCENARIO 3: OFFICE =====
  office: {
    emoji: "👨‍💻", title: "Office Communication", desc: "Professional workplace English",
    greeting: "Good morning! Hope you had a good weekend. Before we start, did you get a chance to review the project update I sent on Friday?",
    keywords: {
      "yes|reviewed|checked|read|saw|looked": [
        "Great! What are your thoughts? Do you think the timeline is realistic?",
        "Perfect. Any concerns or suggestions you'd like to raise?",
        "Glad you had time. Should we discuss any changes in our approach?",
        "Excellent. What stood out to you the most?",
      ],
      "no|didn't|haven't|not yet|busy|forgot": [
        "No worries at all! I'll give you a quick summary. The key takeaway is we're on track but need to allocate more time for testing.",
        "I understand, things have been hectic. Let me walk you through the highlights.",
        "That's fine. When do you think you'll be able to take a look?",
      ],
      "deadline|time|when|schedule|date|due|timeline": [
        "The current deadline is end of next week. Do you think we can meet it?",
        "Let me check the timeline. We might need to adjust if testing takes longer.",
        "Should I send a revised schedule to the team?",
        "If we need an extension, we should communicate that to stakeholders today.",
      ],
      "problem|issue|bug|error|broken|stuck|blocker": [
        "Can you describe the issue in detail? When did it start?",
        "Is it blocking other work? We might need to escalate this.",
        "Let me pull in the relevant team member. Who's best suited to help?",
        "That's concerning. Let's prioritize this and aim to fix it by end of day.",
      ],
      "meeting|discuss|call|sync|agenda|calendar": [
        "Good idea! I'll set up a meeting. Morning or afternoon — what works better?",
        "Let me check everyone's availability and send an invite.",
        "Should we keep it to 30 minutes? I want to be respectful of everyone's time.",
        "I'll prepare an agenda and share it beforehand so we can be productive.",
      ],
      "help|support|need|stuck|assistance|resource": [
        "Of course! What do you need help with? I'm happy to pitch in.",
        "Let me see who's available to support you on this.",
        "Would it help if we paired up on this? Two heads are better than one.",
      ],
      "idea|suggest|proposal|think we should|how about": [
        "I like that idea! Can you flesh it out a bit more?",
        "Interesting approach. What would be the first step to implement it?",
        "That could work! Let's discuss the pros and cons with the team.",
        "Great thinking! Should we create a brief proposal document?",
      ],
      "report|data|numbers|analysis|results|metrics": [
        "The numbers look promising. Can you walk me through the key findings?",
        "Have you identified any trends we should be aware of?",
        "Let's put together a dashboard so stakeholders can track progress.",
        "Great analysis. Can you add a comparison with last quarter's numbers?",
      ],
      "email|message|sent|reply|respond|forward": [
        "I'll send you a follow-up email after our discussion.",
        "Did you CC the relevant stakeholders on that?",
        "I'll draft a response and run it by you before sending. Sound good?",
      ],
      "client|customer|stakeholder|feedback": [
        "What was the client's reaction? Were they satisfied?",
        "Let's make sure we address their concerns before the next meeting.",
        "Customer feedback is gold. Can you share the details with the team?",
      ],
    },
    fallback: [
      "Noted. I'll make sure to follow up on that.",
      "Good point. Let's add that to our action items.",
      "Thanks for flagging that. Anything else we need to cover?",
      "Understood. Let's keep the momentum going.",
    ],
    closing: "Great discussion today! Let me summarize the action items and send them out. Let's check in again tomorrow morning. Have a productive rest of the day!",
  },

  // ===== SCENARIO 4: SHOPPING =====
  shopping: {
    emoji: "🛍️", title: "Shopping", desc: "Buying things, asking about products, negotiating",
    greeting: "Hello and welcome! We're having some great deals today. Are you looking for something specific, or would you like to browse?",
    keywords: {
      "looking for|need|want|find|searching": [
        "Sure! What exactly are you looking for? I can help you find it quickly.",
        "We have a great selection! Can you describe what you have in mind?",
        "Let me show you our most popular items in that category.",
      ],
      "price|cost|how much|expensive|cheap|budget|afford": [
        "This one is $49.99. We also have a similar option for $29.99 if you're on a budget.",
        "Great choice! It's on sale right now — 30% off the original price!",
        "That one's a premium item, but worth every penny. Quality is excellent.",
        "We have options in every price range. What's your budget?",
      ],
      "size|fit|small|medium|large|xl|tight|loose": [
        "What size do you usually wear? We have everything from XS to XXL.",
        "This brand runs a bit small, so I'd recommend going one size up.",
        "Would you like to try it on? The fitting rooms are right around the corner.",
        "We can also do alterations if needed. Takes about 3-5 days.",
      ],
      "color|blue|red|black|white|green|pink|gray": [
        "Great taste! That color is very popular this season.",
        "We have this in 6 different colors. Want me to show you the others?",
        "That color would look great on you! Very versatile for different occasions.",
      ],
      "quality|material|brand|durable|last|fabric": [
        "This is made from 100% organic cotton — very soft and durable.",
        "This brand is known for excellent quality. Customers love it.",
        "It comes with a 2-year warranty, so you're covered if anything goes wrong.",
      ],
      "return|exchange|refund|policy|warranty": [
        "Our return policy is 30 days with receipt. Full refund, no questions asked.",
        "Exchanges are always free! Just bring it back in the original packaging.",
        "We offer a satisfaction guarantee. If you're not happy, we'll make it right.",
      ],
      "recommend|suggest|best|popular|trending|gift": [
        "Our best seller this month is this one — customers absolutely love it!",
        "For a gift, I'd recommend this set. It comes beautifully packaged.",
        "Trending right now is this style. You'll see it everywhere!",
      ],
      "card|cash|pay|payment|upi|online": [
        "We accept all major cards, cash, UPI, and Apple Pay.",
        "Shall I ring that up for you? How would you like to pay?",
        "I can process that right away. Cash or card?",
      ],
      "discount|sale|offer|deal|coupon|code": [
        "You're in luck! We have a buy-one-get-one-half-off deal right now.",
        "If you sign up for our membership, you get 10% off today's purchase.",
        "Let me check if there are any active promo codes I can apply for you.",
      ],
    },
    fallback: [
      "That's an excellent choice! Would you like to see anything else?",
      "Let me check if we have that in stock for you.",
      "Is there anything else I can help you find today?",
      "Shall I put this aside while you continue browsing?",
    ],
    closing: "Thank you so much for shopping with us today! Here's your receipt. We hope you love your purchase. See you again soon! 😊",
  },

  // ===== SCENARIO 5: TRAVEL =====
  travel: {
    emoji: "✈️", title: "Travel & Hotel", desc: "Hotels, airports, directions, tourist info",
    greeting: "Welcome to the Grand Hotel! I'm Priya at the front desk. Are you checking in, or can I help you with something else?",
    keywords: {
      "check in|reservation|booking|book|room|stay": [
        "Wonderful! Could I have your name and booking reference, please?",
        "I've found your reservation. You're in Room 512, a deluxe room with a city view. Here's your key card.",
        "Would you prefer a room on a higher floor? We have some available.",
        "Your room is ready. Checkout is at 11 AM. Would you like a wake-up call?",
      ],
      "breakfast|food|restaurant|eat|dinner|hungry|meal": [
        "Breakfast is served from 6:30 to 10:30 AM in the restaurant on the ground floor.",
        "We have an excellent in-house restaurant. Would you like me to make a reservation?",
        "Room service is available 24/7. Here's the menu. Just dial 0 from your room.",
        "There are also wonderful local restaurants within walking distance. Would you like recommendations?",
      ],
      "taxi|cab|transport|airport|bus|train|uber": [
        "I can arrange a taxi for you right away. Where are you heading?",
        "The airport shuttle leaves every 45 minutes. Next one is at 2:30 PM.",
        "We have complimentary car service to the airport for guests. Let me schedule it.",
        "The nearest metro station is a 5-minute walk. I can show you on the map.",
      ],
      "tourist|visit|attraction|see|place|sightseeing|recommend": [
        "The top attractions are the old fort, the central market, and the botanical gardens.",
        "I'd highly recommend the walking tour — it starts at 9 AM from the lobby.",
        "Here's a city map with the best spots marked. The museum is a must-visit!",
        "For something unique, try the sunset boat tour on the river. Absolutely stunning!",
      ],
      "wifi|internet|password|connection": [
        "The WiFi network is 'GrandGuest' and the password is on your key card holder.",
        "We have complimentary high-speed WiFi throughout the hotel.",
        "If you need a stronger connection for video calls, the business center on floor 2 has dedicated WiFi.",
      ],
      "checkout|leave|bill|receipt|payment": [
        "Checkout is at 11 AM. I can prepare your bill now if you'd like.",
        "Would you like a printed or digital receipt?",
        "Late checkout until 2 PM is available for an additional $25. Would you like that?",
        "It's been a pleasure having you! We hope to see you again.",
      ],
      "pool|gym|spa|fitness|sauna|massage": [
        "The pool is on the rooftop, open from 6 AM to 10 PM. Towels are provided.",
        "Our spa offers excellent massage packages. Want me to book one?",
        "The fitness center is 24 hours, on the 3rd floor. Your key card gives access.",
      ],
      "laundry|clean|iron|wash": [
        "Laundry service is available. Items left before 9 AM are returned by 6 PM.",
        "We also have an express service for urgent items — 3 hour turnaround.",
        "Iron and ironing board are available in your room closet.",
      ],
      "problem|complaint|noise|dirty|cold|hot|broken": [
        "I sincerely apologize for the inconvenience. Let me resolve this immediately.",
        "I'm so sorry to hear that. I'll send someone to fix it right away.",
        "That shouldn't happen. Would you like to move to a different room?",
        "Please accept our apologies. I'll make sure this is taken care of and offer a complimentary upgrade.",
      ],
    },
    fallback: [
      "Of course! I'm here to help. What else can I do for you?",
      "Let me look into that right away.",
      "Is there anything else you need during your stay?",
      "Don't hesitate to call the front desk anytime — we're available 24/7.",
    ],
    closing: "Thank you for choosing Grand Hotel! We hope you had a wonderful stay. Your feedback means the world to us. Have a safe journey! 🌟",
  },

  // ===== SCENARIO 6: MEDICAL =====
  medical: {
    emoji: "👨‍⚕️", title: "Doctor's Visit", desc: "Describing symptoms, understanding medical advice",
    greeting: "Good afternoon, I'm Dr. Patel. Please have a seat. How are you feeling today? What brings you to see me?",
    keywords: {
      "head|headache|migraine|pain in head|dizzy|dizziness": [
        "I'm sorry to hear that. How long have you been experiencing headaches?",
        "On a scale of 1 to 10, how severe is the pain right now?",
        "Is it a sharp pain or more of a dull ache? Does anything trigger it?",
        "Have you noticed it getting worse at certain times — morning, evening, after screens?",
        "Do you have any sensitivity to light or sound during these episodes?",
      ],
      "cold|cough|fever|flu|throat|sneeze|nose|blocked|runny": [
        "Those sound like common cold symptoms. When did they start?",
        "Have you checked your temperature? Any fever above 100°F?",
        "Are you experiencing any body aches along with the cold?",
        "I'll prescribe some medication. In the meantime, rest and drink lots of warm fluids.",
        "If symptoms persist beyond a week, please come back for a follow-up.",
      ],
      "stomach|nausea|vomit|digest|appetite|bloating|cramp": [
        "When did the stomach issues begin? Was it after eating something specific?",
        "Are you experiencing any nausea, vomiting, or diarrhea?",
        "Any changes in your diet recently? Sometimes new foods can cause issues.",
        "I'd recommend sticking to a bland diet — rice, toast, bananas — for a few days.",
        "Let me check for any tenderness. Does it hurt when I press here?",
      ],
      "tired|fatigue|energy|exhausted|weak|sleepy|insomnia": [
        "Fatigue can have many causes. How many hours of sleep are you getting?",
        "Have you been under more stress than usual lately?",
        "I'd like to run some blood tests — check your iron, vitamin D, and thyroid levels.",
        "Are you staying hydrated? Dehydration is a common cause of fatigue.",
        "Regular exercise actually helps with energy levels. Even a 20-minute walk can help.",
      ],
      "back|spine|shoulder|neck|muscle|joint|knee|leg|arm": [
        "Is the pain constant or does it come and go?",
        "Did this start after any physical activity or injury?",
        "I'm going to recommend some physiotherapy sessions. How does that sound?",
        "Try applying ice for 15 minutes every few hours. Avoid heavy lifting for now.",
        "If the pain doesn't improve in 2 weeks, we may need to do an X-ray.",
      ],
      "skin|rash|itch|allergy|bump|red|swelling": [
        "When did you first notice the rash? Has it been spreading?",
        "Have you started using any new products — soap, detergent, food?",
        "This looks like a mild allergic reaction. I'll prescribe an antihistamine.",
        "Apply this cream twice daily. If it spreads or worsens, come back immediately.",
      ],
      "medicine|medication|prescription|drug|pill|tablet|dose": [
        "Are you currently taking any medications I should know about?",
        "Take this twice daily with food — morning and evening.",
        "Do you have any known drug allergies?",
        "Complete the full course even if you start feeling better. That's very important.",
        "You can get this filled at any pharmacy. It's a common prescription.",
      ],
      "test|check|blood|x-ray|scan|examine|lab": [
        "I'd like to run some tests to get a clearer picture. Is that okay?",
        "We can do the blood work right here. Results will be ready in 2 days.",
        "Based on your symptoms, I think an X-ray would be helpful.",
        "The test results are normal — that's good news! Let's monitor your symptoms.",
      ],
      "anxiety|stress|mental|depression|panic|worried|sleep": [
        "Mental health is just as important as physical health. Tell me more about what you're experiencing.",
        "How long have you been feeling this way? Has anything changed in your life recently?",
        "I can refer you to a counselor who specializes in this. Would you be open to that?",
        "In the meantime, try some breathing exercises and limit caffeine. It can really help.",
        "You're not alone in this. It's very common and very treatable.",
      ],
      "exercise|diet|weight|healthy|lifestyle|prevent": [
        "Regular exercise — even 30 minutes of walking — makes a huge difference.",
        "Try to eat more fruits, vegetables, and whole grains. Reduce processed foods.",
        "Hydration is key. Aim for at least 8 glasses of water a day.",
        "Getting 7-8 hours of sleep is one of the best things you can do for your health.",
        "Prevention is always better than cure. Small lifestyle changes go a long way!",
      ],
    },
    fallback: [
      "I see. Can you describe your symptoms in a bit more detail?",
      "How is this affecting your daily activities?",
      "Have you experienced anything like this before?",
      "That's important information. Let me note that down.",
      "Is there anything else you've noticed that might be related?",
    ],
    closing: "Here's your prescription. Take the medication as directed and get plenty of rest. If symptoms don't improve within a week, please schedule a follow-up. Take care of yourself — you're in good hands! 😊",
  },

  // ===== SCENARIO 7: ACADEMIC =====
  academic: {
    emoji: "👨‍🏫", title: "Academic English", desc: "University discussions, essays, presentations",
    greeting: "Good morning, everyone! Welcome to today's session. Before we dive in, does anyone have questions about the upcoming essay assignment?",
    keywords: {
      "topic|subject|write about|essay|paper|thesis": [
        "Choose a topic you're genuinely interested in — passion makes writing easier.",
        "Your topic should be specific enough to cover thoroughly in 1500 words.",
        "Good topics include: impact of social media on communication, climate change solutions, or AI in education.",
        "Make sure your thesis statement is clear and arguable. What's your position?",
      ],
      "deadline|submit|when|due|extension|late": [
        "The essay is due next Friday at 11:59 PM. Submit via the online portal.",
        "Late submissions lose 10% per day. No exceptions unless you have medical documentation.",
        "If you need an extension, email me BEFORE the deadline with a valid reason.",
        "Pro tip: try to finish 2 days early so you have time to revise.",
      ],
      "help|don't understand|confused|difficult|hard|stuck": [
        "No worries! Which specific part is giving you trouble?",
        "Let's break it down step by step. What have you done so far?",
        "Office hours are Tuesday and Thursday, 2-4 PM. Come see me — that's what they're for!",
        "Try explaining the concept in your own words first. Often that clarifies things.",
        "The tutoring center is also a great resource. Free for all students.",
      ],
      "grade|mark|score|result|pass|fail|criteria": [
        "I evaluate based on: argument quality (30%), evidence (25%), structure (25%), and grammar (20%).",
        "An A-grade essay has a clear thesis, strong evidence, logical structure, and polished writing.",
        "Don't just summarize — analyze, compare, and give your own reasoned perspective.",
        "I'll provide detailed feedback so you know exactly how to improve for next time.",
      ],
      "reference|source|cite|bibliography|plagiarism": [
        "Use at least 5 academic sources. Wikipedia and random blogs don't count.",
        "APA citation format, please. I'll share a style guide on the class portal.",
        "Plagiarism is taken very seriously. Always cite your sources. When in doubt, cite!",
        "Google Scholar and our library database are your best friends for research.",
      ],
      "presentation|present|speak|slide|powerpoint": [
        "Keep slides minimal — bullet points, not paragraphs. You are the presentation, not the slides.",
        "Practice out loud at least 3 times before presenting. Time yourself!",
        "Eye contact is important. Look at your audience, not the screen.",
        "Prepare for questions after. Think about what people might ask and have answers ready.",
      ],
      "group|team|partner|project|collaborate": [
        "For group projects, assign clear roles from day one. Who does what by when.",
        "Use shared documents so everyone can see progress in real time.",
        "Communication is key. Set up a group chat and have weekly check-ins.",
        "If there's a conflict in your group, come talk to me. I can help mediate.",
      ],
      "exam|test|final|midterm|prepare|study|review": [
        "The exam covers chapters 1-8 and all lecture topics. Focus on key concepts.",
        "Past exam papers are available in the library. Great for practice!",
        "Study in active ways — make flashcards, teach someone, do practice problems.",
        "Get good sleep before the exam. An all-nighter does more harm than good.",
      ],
    },
    fallback: [
      "That's a thoughtful point! Anyone else want to add to that?",
      "Good question. Let me think about the best way to explain this.",
      "I appreciate your engagement with the material. Can you expand on that?",
      "Interesting perspective! How does that connect to what we discussed last week?",
    ],
    closing: "Excellent discussion today! Remember, the essay is due next Friday. Start early, plan your structure, and don't hesitate to reach out if you need help. See you next class! 📚",
  },

  // ===== SCENARIO 8: PHONE CALLS =====
  phone: {
    emoji: "📞", title: "Phone Calls", desc: "Customer support, business calls, appointments",
    greeting: "Good afternoon, thank you for calling TechSupport Plus. My name is Alex. How may I assist you today?",
    keywords: {
      "order|delivery|shipping|package|track|arrive": [
        "I'd be happy to help with that. Could you provide your order number?",
        "Let me look that up... Your order was shipped yesterday and should arrive within 3-5 business days.",
        "I can see it's currently in transit. Would you like the tracking number?",
        "I apologize for the delay. Let me check what happened and expedite it for you.",
      ],
      "refund|return|cancel|money back|charge|overcharged": [
        "I understand you'd like a refund. Could you tell me the reason?",
        "We can process a full refund within 5-7 business days. Would you like to proceed?",
        "I see the charge on your account. That was an error and I'll reverse it right now.",
        "Would you prefer a refund to your original payment method or store credit?",
      ],
      "problem|issue|broken|not working|error|defective|damaged": [
        "I'm sorry to hear that. Can you describe exactly what's happening?",
        "Have you tried restarting the device? Sometimes that resolves the issue.",
        "It sounds like a hardware issue. We can arrange a replacement. Would you like that?",
        "I'm going to escalate this to our technical team. You'll receive an update within 24 hours.",
      ],
      "account|password|login|access|locked|forgot|reset": [
        "Let me verify your identity first. Can you confirm the email on your account?",
        "I've sent a password reset link to your registered email address.",
        "Your account has been unlocked. Please try logging in again now.",
        "For security purposes, I've enabled two-factor authentication on your account.",
      ],
      "manager|supervisor|complaint|escalate|unhappy|frustrated": [
        "I completely understand your frustration. Let me see what I can do first.",
        "I want to make sure this is resolved to your satisfaction. Let me try one more thing.",
        "I'll connect you with a senior representative who can assist further. Please hold.",
        "I'm documenting this and will make sure it gets proper attention.",
      ],
      "appointment|schedule|book|available|time|when": [
        "Let me check our availability. What day works best for you?",
        "We have openings on Tuesday at 2 PM and Thursday at 10 AM. Which do you prefer?",
        "Your appointment is confirmed. You'll receive a confirmation email shortly.",
        "Would you like a reminder call the day before your appointment?",
      ],
      "bill|invoice|payment|charge|statement|balance": [
        "I can pull up your billing statement. Your current balance is $45.99.",
        "The charge you're seeing is for your monthly subscription. Would you like more details?",
        "I can set up automatic payments if you'd like. Would that be helpful?",
      ],
      "thank|thanks|helpful|appreciate|resolved|great": [
        "You're very welcome! I'm glad I could help.",
        "Thank you for your patience! Is there anything else I can assist with?",
        "It was my pleasure! Don't hesitate to call back if you need anything.",
        "Happy I could resolve that for you. Have a wonderful rest of your day!",
      ],
    },
    fallback: [
      "I understand. Let me look into that for you.",
      "Could you provide a few more details so I can assist you better?",
      "I want to make sure I understand correctly. You're saying...?",
      "Let me check on that. Please bear with me for just a moment.",
    ],
    closing: "Thank you for calling TechSupport Plus! Your reference number is #TS-47829. Is there anything else I can help with? Have a wonderful day! 😊",
  },

  // ===== NEW SCENARIO 9: RESTAURANT =====
  restaurant: {
    emoji: "🍽️", title: "Restaurant", desc: "Ordering food, making reservations, complaints",
    greeting: "Good evening! Welcome to The Garden Bistro. Do you have a reservation, or would you like a table for how many?",
    keywords: {
      "table|seat|reservation|book|party|two|three|four": [
        "I have a lovely table by the window available. Would that work?",
        "Your reservation is confirmed. Right this way, please.",
        "The wait time is about 15 minutes. Would you like to wait at the bar?",
        "Excellent! Let me seat you. Do you have any seating preferences?",
      ],
      "menu|order|ready|decide|recommend|special|today": [
        "Here's the menu. Our specials today are grilled salmon and mushroom risotto.",
        "Our chef's recommendation tonight is the herb-crusted lamb. Absolutely delicious!",
        "Take your time looking at the menu. Can I get you something to drink while you decide?",
        "The pasta is made fresh daily. It's one of our most popular dishes!",
      ],
      "vegetarian|vegan|gluten|allergy|dairy|nut|spicy|mild": [
        "We have several vegetarian options marked with a 'V' on the menu.",
        "Our vegan menu includes the Buddha bowl, vegetable curry, and roasted cauliflower steak.",
        "I'll make sure the kitchen knows about your allergy. Safety first!",
        "We can adjust the spice level to your preference. Mild, medium, or hot?",
      ],
      "drink|wine|beer|cocktail|water|juice|coffee|tea": [
        "Can I suggest a wine? Our house red pairs beautifully with the steak.",
        "We have a wonderful cocktail menu. The mango mojito is a customer favorite!",
        "Still or sparkling water? And would you like lemon with that?",
        "Our coffee is locally roasted. Perfect way to end the meal!",
      ],
      "bill|check|pay|card|split|separate|tip": [
        "I'll bring the check right over. How would you like to pay?",
        "Would you like to split the bill, or shall I put it on one check?",
        "We accept all major credit cards and cash.",
        "Service charge is included in the bill. Any additional tip is appreciated!",
      ],
      "good|delicious|excellent|amazing|wonderful|tasty|love": [
        "I'm so glad you enjoyed it! I'll pass your compliments to the chef.",
        "That makes our day! Would you like to try our dessert menu?",
        "Thank you! We take pride in our food. Can I interest you in anything else?",
      ],
      "cold|wait|wrong|slow|complaint|rude|overcooked|undercooked": [
        "I sincerely apologize for that. Let me get the manager right away.",
        "That's not acceptable and I'm very sorry. Let me replace that immediately.",
        "I understand your frustration. We'll make this right — this meal is on the house.",
        "Thank you for your feedback. We'll use it to improve our service.",
      ],
      "dessert|sweet|cake|ice cream|chocolate": [
        "Our dessert menu is wonderful! The tiramisu and chocolate lava cake are favorites.",
        "I'd recommend the crème brûlée — made fresh every day.",
        "Can I tempt you with our homemade gelato? We have 6 flavors today!",
      ],
    },
    fallback: [
      "Excellent choice! I'll put that order in right away.",
      "How is everything so far? Can I get you anything else?",
      "Take your time and enjoy. I'll check back in a few minutes.",
    ],
    closing: "Thank you for dining with us tonight! We hope you had a wonderful experience. We look forward to welcoming you back soon! 🍽️",
  },

  // ===== NEW SCENARIO 10: BANK =====
  bank: {
    emoji: "🏦", title: "Banking", desc: "Opening accounts, loans, transactions, complaints",
    greeting: "Good morning! Welcome to National Bank. I'm Meera, your personal banker. How can I help you today?",
    keywords: {
      "account|open|savings|current|checking|new": [
        "We'd be happy to help you open an account. We have savings and current account options.",
        "For a savings account, you'll need a photo ID and proof of address. Do you have those?",
        "Our premium savings account offers 4.5% interest. Would you like the details?",
        "The process takes about 15 minutes. Your card will be ready in 5 business days.",
      ],
      "loan|borrow|mortgage|emi|interest rate|credit": [
        "What type of loan are you looking for — personal, home, or vehicle?",
        "Our current personal loan rate starts at 8.5%. The amount depends on your income.",
        "For a home loan, we offer up to 80% financing with competitive interest rates.",
        "I can run a quick eligibility check. Could you share your monthly income?",
      ],
      "transfer|send|receive|payment|transaction|wire": [
        "I can help with that. Do you need a domestic or international transfer?",
        "You'll need the recipient's account number, bank name, and IFSC code.",
        "Online transfers through our app are instant and free. Have you set up mobile banking?",
        "For large amounts, I'd recommend a wire transfer for security. Let me initiate that.",
      ],
      "balance|statement|check|how much|details": [
        "I can pull up your account details. Could you provide your account number?",
        "Your current balance is available on our mobile app 24/7.",
        "Would you like a printed statement? I can provide the last 3 months.",
      ],
      "card|debit|credit|atm|pin|lost|stolen|block": [
        "You can apply for a credit card right now. We have several options based on your needs.",
        "If your card is lost, I'll block it immediately and issue a replacement.",
        "Your new card will arrive within 5-7 business days. Here's a temporary PIN.",
        "Our premium credit card offers cashback, lounge access, and zero annual fee for the first year.",
      ],
      "problem|issue|wrong|error|charge|fraud|unauthorized": [
        "I'm sorry to hear that. Can you describe the issue in detail?",
        "If you've noticed unauthorized transactions, we'll freeze the account immediately for protection.",
        "I'll file a dispute form right now. Investigation typically takes 7-10 business days.",
        "Rest assured, your money is protected. We take security very seriously.",
      ],
      "invest|mutual fund|fixed deposit|fd|returns": [
        "We offer various investment options. Fixed deposits are our safest option at 6.5% per annum.",
        "Mutual funds offer higher returns but with some market risk. Would you like guidance?",
        "I can set up a systematic investment plan starting from just $100 per month.",
      ],
    },
    fallback: [
      "Let me check that for you. One moment, please.",
      "That's a great question. Here's what I recommend...",
      "Is there anything else related to your banking needs today?",
    ],
    closing: "Thank you for choosing National Bank! Here's my card — don't hesitate to call if you need anything. Have a wonderful day! 🏦",
  },

  // ===== NEW SCENARIO 11: REAL ESTATE =====
  realestate: {
    emoji: "🏠", title: "House Hunting", desc: "Renting or buying a house, talking to agents",
    greeting: "Hello! Welcome to HomeFind Realty. I'm Sarah. Are you looking to buy or rent? Tell me what you have in mind!",
    keywords: {
      "buy|purchase|own|invest|property": [
        "Exciting! What's your budget range? And are you looking for an apartment or a house?",
        "Do you have a preferred location? Proximity to work, schools, or transport?",
        "The market is quite active right now. Let me show you some great listings.",
        "Have you been pre-approved for a mortgage? That helps us narrow down options.",
      ],
      "rent|lease|monthly|tenant|deposit": [
        "We have several rental properties available. What's your monthly budget?",
        "Most rentals require a security deposit equal to 2 months' rent.",
        "How long are you planning to stay? We have 1-year and 2-year lease options.",
        "Utilities are typically extra. Some properties include water and internet though.",
      ],
      "bedroom|bathroom|room|space|size|area|sqft": [
        "How many bedrooms do you need? We have 1BHK to 4BHK options.",
        "This property is 1200 square feet with 3 bedrooms and 2 bathrooms.",
        "Would you like a separate study or home office space?",
      ],
      "location|area|neighborhood|near|close|transport|school": [
        "This neighborhood is very family-friendly with great schools nearby.",
        "The metro station is just a 5-minute walk from this property.",
        "It's in a gated community with 24/7 security, parks, and a clubhouse.",
      ],
      "price|cost|expensive|cheap|negotiate|offer|deal": [
        "This property is listed at $350,000. It's competitively priced for the area.",
        "There might be room for negotiation. Would you like to make an offer?",
        "I can help you structure a competitive offer. What's your maximum budget?",
      ],
      "visit|see|tour|viewing|when|available": [
        "I can arrange a viewing this weekend. Would Saturday morning work?",
        "We also offer virtual tours if you'd like a preview first.",
        "The property is available for immediate viewing. Shall we go now?",
      ],
    },
    fallback: [
      "That's a great question about the property. Let me find out for you.",
      "I have a few properties that might match what you're looking for.",
      "Would you like me to set up property alerts based on your preferences?",
    ],
    closing: "Thank you for considering HomeFind Realty! I'll send you a list of properties matching your criteria. Don't hesitate to call with any questions. Happy house hunting! 🏠",
  },

  // ===== NEW SCENARIO 12: TECH SUPPORT =====
  techsupport: {
    emoji: "💻", title: "Tech Support", desc: "Fixing computer/phone issues, troubleshooting",
    greeting: "Hi! Welcome to Tech Help. I'm here to solve any tech issues you're having. What device are you using, and what seems to be the problem?",
    keywords: {
      "slow|lag|freeze|hanging|stuck|loading": [
        "A slow device is frustrating! How long has it been like this?",
        "Try clearing your cache and closing unnecessary apps. That often helps.",
        "How much storage space do you have left? Low storage can cause slowness.",
        "Have you tried restarting it? I know it sounds simple, but it fixes many issues!",
      ],
      "wifi|internet|connection|network|disconnect|connect": [
        "Let's troubleshoot your WiFi. Can you see the network name in your settings?",
        "Try turning WiFi off and on again. If that doesn't work, restart your router.",
        "Are other devices connecting fine? That helps us isolate the problem.",
        "You might need to forget the network and reconnect. Go to WiFi settings and tap 'Forget'.",
      ],
      "password|login|forgot|locked|access|account": [
        "Click 'Forgot Password' on the login page and check your email for a reset link.",
        "Make sure Caps Lock is off — passwords are case-sensitive.",
        "If you're still locked out, I can help you recover your account. What's the email address?",
        "Pro tip: use a password manager so you never forget again!",
      ],
      "update|upgrade|version|install|download": [
        "What version are you currently running? Check in Settings > About.",
        "I'd recommend updating to the latest version. It usually fixes bugs.",
        "Make sure you have enough storage before updating. Back up your data first!",
        "The update might take 15-30 minutes. Make sure your device is plugged in.",
      ],
      "virus|malware|spam|hack|security|suspicious": [
        "Don't click on anything suspicious! Let me guide you through removing it.",
        "Run a full system scan with your antivirus software. Do you have one installed?",
        "Change your passwords immediately, especially for email and banking.",
        "I recommend enabling two-factor authentication for all important accounts.",
      ],
      "crash|error|blue screen|restart|shutdown|won't turn on": [
        "When does the crash happen? During specific apps or randomly?",
        "Try holding the power button for 10 seconds for a force restart.",
        "A blue screen usually means a driver issue. Have you installed any new software recently?",
        "If it won't turn on at all, try charging it for 30 minutes, then hold power + volume down together.",
      ],
      "app|install|delete|uninstall|not working|broken": [
        "Try uninstalling the app, restart your device, then reinstall it from the store.",
        "Make sure the app is compatible with your device version.",
        "Clear the app's cache and data — that often resolves glitches.",
        "If the app keeps crashing, check for updates in the app store.",
      ],
      "data|backup|restore|lost|recover|storage|cloud": [
        "Do you have cloud backup enabled? Check iCloud or Google Drive settings.",
        "If data is lost, stop using the device immediately — recovery tools might help.",
        "I recommend setting up automatic backups. It takes 2 minutes and saves so much stress!",
        "For photos, Google Photos or iCloud can automatically back up everything.",
      ],
    },
    fallback: [
      "Let me think about the best solution for this. Can you try one thing for me?",
      "That's an unusual issue. Can you send me a screenshot?",
      "Have you tried turning it off and on again? Seriously, it works more often than you'd think!",
      "Let me walk you through this step by step.",
    ],
    closing: "Glad we could fix that! Remember, most tech issues can be solved with updates, restarts, and clearing cache. If you need help again, we're always here! Happy computing! 💻",
  },

  // ===== NEW SCENARIO 13: FITNESS TRAINER =====
  fitness: {
    emoji: "💪", title: "Fitness & Gym", desc: "Exercise plans, nutrition, gym conversations",
    greeting: "Hey there! Welcome to FitLife Gym! I'm Coach Mike. Are you new here, or looking to change up your routine?",
    keywords: {
      "new|beginner|start|first time|never|join": [
        "Welcome aboard! Everyone starts somewhere. What are your fitness goals?",
        "For beginners, I recommend starting with 3 days a week. We'll build up gradually.",
        "Let me show you around the gym and explain the basic equipment.",
        "Don't worry about what others are doing. Focus on your own journey!",
      ],
      "lose weight|fat|slim|diet|calories|burn": [
        "Weight loss is 70% diet and 30% exercise. Let's work on both!",
        "I recommend a mix of cardio and strength training for fat loss.",
        "How's your current diet? Are you tracking what you eat?",
        "Consistency is more important than intensity. Show up regularly and results will come!",
      ],
      "muscle|strong|build|gain|bulk|protein|lift": [
        "For muscle building, focus on compound exercises — squats, deadlifts, bench press.",
        "Make sure you're eating enough protein — about 1.6g per kg of body weight.",
        "Progressive overload is key — gradually increase the weight each week.",
        "Rest days are when muscles actually grow. Don't skip them!",
      ],
      "plan|routine|program|schedule|workout": [
        "I'll create a personalized plan based on your goals. How many days can you commit?",
        "A good split would be: push/pull/legs with 2 rest days.",
        "Your workout should last 45-60 minutes. Quality over quantity!",
        "I'll write everything down so you know exactly what to do each day.",
      ],
      "pain|injury|hurt|sore|strain|careful": [
        "If something hurts, stop immediately. Pain is not gain — it's a warning!",
        "That sounds like it might need rest. Have you seen a physiotherapist?",
        "Warm up before every workout and stretch after. It prevents a lot of injuries.",
        "RICE — Rest, Ice, Compression, Elevation. That's the first aid for most gym injuries.",
      ],
      "motivation|quit|lazy|skip|tired|can't|give up": [
        "Everyone has those days! The fact that you're here shows you care.",
        "Don't focus on motivation — build discipline. Just show up, even for 20 minutes.",
        "Remember why you started. Your future self will thank you!",
        "Progress is not always visible, but it's happening. Trust the process!",
        "Let's set small, achievable goals. Small wins build momentum!",
      ],
    },
    fallback: [
      "That's a great question! Let me share what I've seen work best.",
      "Keep pushing! You're doing better than you think.",
      "Everyone's body is different. Let's find what works for you.",
    ],
    closing: "Great session today! Remember — consistency beats perfection every time. See you at the gym! Keep pushing! 💪🔥",
  },

  // ===== NEW SCENARIO 14: LANDLORD/TENANT =====
  landlord: {
    emoji: "🏢", title: "Landlord & Tenant", desc: "Renting issues, maintenance, lease discussions",
    greeting: "Hello, this is Mr. Sharma, your building manager. How can I help you today? Is everything okay with your apartment?",
    keywords: {
      "repair|fix|broken|leak|maintenance|plumber|electrician": [
        "I'll send a maintenance person over today. What exactly needs fixing?",
        "That sounds urgent. I'll make it a priority and have someone there within 2 hours.",
        "Please don't try to fix it yourself — we have professionals for that.",
        "I apologize for the inconvenience. We'll take care of it as soon as possible.",
      ],
      "rent|pay|late|increase|deposit|dues": [
        "Your rent is due on the 1st of each month. Would you like to set up auto-pay?",
        "I understand things can be tight. Can you pay by the 5th? We allow a grace period.",
        "The rent increase is in line with the market rate. I can show you the comparison.",
        "Your deposit will be refunded within 30 days of move-out, minus any damages.",
      ],
      "noise|neighbor|complaint|loud|party|disturbance": [
        "I'm sorry about the noise. I'll speak to the neighbor about quiet hours.",
        "Our building policy requires quiet after 10 PM. I'll send a reminder to all residents.",
        "If it continues, please document the incidents and I'll take formal action.",
      ],
      "move|leave|vacate|notice|terminate|end lease": [
        "Standard notice period is 30 days. Please submit a written notice.",
        "I'm sorry to see you go. Is there anything we can do to make you stay?",
        "We'll schedule a move-out inspection to process your deposit refund.",
      ],
      "pest|bug|cockroach|ant|rat|mouse|insect": [
        "We'll schedule pest control this week. Which days work for you?",
        "In the meantime, keep food sealed and clean up crumbs to reduce the issue.",
        "Pest control is covered by the building. No charge to you.",
      ],
    },
    fallback: [
      "I understand your concern. Let me look into this right away.",
      "That's noted. I'll make sure it's addressed as soon as possible.",
      "Is there anything else about your apartment that needs attention?",
    ],
    closing: "Thank you for bringing this to my attention. We want to make sure you're comfortable in your home. Don't hesitate to contact me anytime!",
  },

  // ===== NEW SCENARIO 15: NETWORKING EVENT =====
  networking: {
    emoji: "🤝", title: "Networking Event", desc: "Professional introductions, small talk, exchanging contacts",
    greeting: "Hi there! Great event, isn't it? I'm Priya from TechVentures. What brings you here today?",
    keywords: {
      "work|company|job|role|position|do|industry": [
        "Oh interesting! What does your day-to-day look like in that role?",
        "That's a fascinating industry! How did you get into it?",
        "I've heard great things about that company. What do you enjoy most about working there?",
        "That sounds like a dynamic role. What's the most exciting project you're working on?",
      ],
      "startup|business|founder|idea|launch|build": [
        "A startup? That takes courage! What problem are you solving?",
        "How far along are you? Have you gotten any funding yet?",
        "I'd love to hear your elevator pitch! Give me the 30-second version.",
        "The startup world is exciting. What's been your biggest challenge so far?",
      ],
      "connect|linkedin|card|email|contact|follow up": [
        "I'd love to stay in touch! Here's my card. Do you have LinkedIn?",
        "Let me add you on LinkedIn right now. What's your full name?",
        "Great meeting you! Let's schedule a coffee chat next week.",
        "I'll send you an email tomorrow. Let's definitely continue this conversation!",
      ],
      "learn|speaker|session|talk|presentation|panel": [
        "The keynote was incredible, wasn't it? What was your key takeaway?",
        "I'm really looking forward to the panel discussion after the break.",
        "Have you attended this event before? Any sessions you'd recommend?",
      ],
      "mentor|advice|guide|help|experience|career": [
        "I'm always happy to share my experience. What specifically would be helpful?",
        "The best advice I ever got was: 'Network before you need to.' So here we are!",
        "Finding a good mentor can change your career. What area do you want guidance in?",
      ],
    },
    fallback: [
      "That's really interesting! Tell me more about that.",
      "I love meeting people with diverse perspectives. What's your take on the industry trends?",
      "Great chatting with you! Should we grab a coffee?",
    ],
    closing: "It was absolutely wonderful meeting you! I really enjoyed our conversation. Let's definitely keep in touch — I'll follow up via LinkedIn. Enjoy the rest of the event! 🤝",
  },

  // ===== NEW SCENARIO 16: EMERGENCY =====
  emergency: {
    emoji: "🚨", title: "Emergency Situations", desc: "Calling for help, describing emergencies, giving directions",
    greeting: "911, what's your emergency? Please stay calm and tell me what happened.",
    keywords: {
      "fire|smoke|burning|flames": [
        "Is everyone out of the building? Are there any people trapped inside?",
        "I'm dispatching the fire department right now. What's your address?",
        "Stay low if there's smoke. Get out of the building and wait across the street.",
        "Do NOT try to fight the fire yourself. Your safety is the priority.",
      ],
      "accident|crash|car|hit|collision|injured|hurt": [
        "How many vehicles are involved? Are there any injuries?",
        "I'm sending paramedics and police right now. Can you give me the exact location?",
        "If someone is injured, don't move them unless there's immediate danger.",
        "Turn on your hazard lights and stay clear of traffic if possible.",
      ],
      "robbery|theft|stolen|break in|burglar|intruder": [
        "Are you in a safe place right now? Is the intruder still there?",
        "I'm dispatching police to your location. Stay on the line with me.",
        "Don't confront the intruder. Lock yourself in a room if possible.",
        "Can you describe what happened and what was taken?",
      ],
      "medical|heart|breathing|fainted|unconscious|blood|fall": [
        "An ambulance is on its way. Is the person conscious and breathing?",
        "If they're not breathing, do you know CPR? I can guide you through it.",
        "Keep them comfortable and don't give them food or water.",
        "Put pressure on the wound with a clean cloth to stop the bleeding.",
      ],
      "lost|missing|child|help|scared|alone": [
        "Stay where you are if possible. Help is on the way.",
        "Can you describe the person who is missing? What are they wearing?",
        "When was the last time you saw them? Where were they?",
        "Try to stay calm. We're going to find them. I need you to help me with details.",
      ],
      "address|location|where|street|near|building": [
        "Can you give me the street name and building number?",
        "What's the nearest landmark or cross street?",
        "Are you inside a building or outside? Which floor?",
      ],
    },
    fallback: [
      "Help is on the way. Please stay on the line with me.",
      "Can you tell me more about what's happening right now?",
      "You're doing great staying calm. Help will be there very soon.",
    ],
    closing: "Help is on its way and should arrive within minutes. Stay safe and don't hang up if you need anything else. You handled this very well. 🚨",
  },

  // ===== NEW SCENARIO 17: IMMIGRATION / VISA =====
  immigration: {
    emoji: "🛂", title: "Immigration & Visa", desc: "Visa applications, airport immigration, customs",
    greeting: "Good day. Welcome to the immigration counter. May I see your passport and visa documents, please?",
    keywords: {
      "passport|document|id|identification|papers": [
        "Thank you. Let me verify your passport. What is the purpose of your visit?",
        "Your passport looks valid. Do you have any supporting documents?",
        "I see your passport expires in 3 months. You may want to renew it soon.",
        "Is this your first time traveling with this passport?",
      ],
      "tourist|vacation|holiday|visit|sightseeing|travel": [
        "How long do you plan to stay? Tourist visas are usually valid for 30-90 days.",
        "Do you have a return ticket booked? That's required for tourist visas.",
        "Where will you be staying during your visit? Do you have a hotel booking?",
        "What places are you planning to visit? Do you have an itinerary?",
      ],
      "work|job|employment|business|company|meeting": [
        "Do you have a work visa or a business invitation letter?",
        "Which company will you be working with? Do you have the offer letter?",
        "Business visas allow meetings but not employment. Is this a short visit?",
        "You'll need your employer's sponsorship letter. Do you have that?",
      ],
      "study|student|university|college|course|education": [
        "Do you have your student visa and admission letter from the university?",
        "How long is your course? Student visas need to match your study duration.",
        "Have you arranged accommodation near your university?",
        "Do you have proof of financial support for your studies?",
      ],
      "how long|duration|stay|days|weeks|months|return": [
        "Your visa allows you to stay for 90 days. When do you plan to leave?",
        "Make sure you don't overstay your visa. That can cause serious problems.",
        "If you need to extend your stay, visit the immigration office before your visa expires.",
      ],
      "declare|customs|goods|items|bring|carry|luggage": [
        "Do you have anything to declare? Any food, plants, or large amounts of cash?",
        "Electronics over $500 in value should be declared.",
        "Certain items are restricted. Please check the customs guidelines.",
        "If you're unsure, it's always better to declare. You won't be penalized for declaring.",
      ],
      "denied|rejected|problem|issue|refused|ban": [
        "I'm sorry, but there seems to be an issue with your application. Let me explain.",
        "You can appeal the decision within 30 days. I'll give you the form.",
        "The most common reasons for denial are insufficient documents or financial proof.",
        "I'd recommend consulting an immigration lawyer for your specific situation.",
      ],
      "family|spouse|husband|wife|children|dependent": [
        "Are your family members traveling with you? They'll need their own visas.",
        "Dependent visas are available for spouses and children under 18.",
        "Family reunion visas have specific requirements. Would you like the details?",
      ],
    },
    fallback: [
      "Let me check your documents. One moment, please.",
      "Do you have any other supporting documents?",
      "Is there anything else about your travel plans I should know?",
      "Everything looks good so far. Just a few more questions.",
    ],
    closing: "Everything checks out. Welcome! Your visa is valid for the duration stated. Please keep your documents safe. Enjoy your stay and follow the local laws. Safe travels! 🛂",
  },

  // ===== NEW SCENARIO 18: DATING / SOCIAL =====
  dating: {
    emoji: "💬", title: "Social & Dating", desc: "Making friends, casual dates, social situations",
    greeting: "Hey! Nice to meet you! I feel like I've seen you around before. What's your name?",
    keywords: {
      "name|call me|i'm|i am": [
        `Nice to meet you${context.name ? ', ' + context.name : ''}! So what do you do for fun?`,
        "That's a lovely name! Where are you from originally?",
        "Great to put a name to the face! So tell me — what's your story?",
      ],
      "work|job|do for a living|career|profession": [
        "That sounds really interesting! Do you enjoy it?",
        "Oh cool! How did you get into that field?",
        "That must keep you busy! What's the best part about your job?",
        "Sounds like a great career! Is it what you always wanted to do?",
      ],
      "hobby|fun|free time|enjoy|like to|interest|passion": [
        "That's awesome! How long have you been into that?",
        "We actually have that in common! I enjoy that too!",
        "That says a lot about you — very cool! Have you gotten really good at it?",
        "I've always wanted to try that! Is it hard to get started?",
      ],
      "movie|music|book|show|series|read|watch|listen|song|band": [
        "Oh I love talking about this stuff! What's your all-time favorite?",
        "Great taste! Have you watched/listened to anything good recently?",
        "We should swap recommendations! I think you'd love what I've been into lately.",
        "That's such a good choice! What is it about that one that you love?",
      ],
      "food|eat|cook|restaurant|coffee|drink|favorite": [
        "Food is the way to my heart! What's your absolute favorite cuisine?",
        "Are you a foodie? Do you cook or prefer eating out?",
        "There's this amazing place I know — we should check it out sometime!",
        "Oh yes! I could eat that every day and never get bored!",
      ],
      "travel|been|country|place|trip|visit|abroad": [
        "I love hearing about travel! What's the most amazing place you've visited?",
        "That's on my bucket list! What was the highlight of that trip?",
        "Where do you want to go next? I'm always planning my next adventure!",
        "Travel changes you, doesn't it? What's your most memorable experience abroad?",
      ],
      "funny|laugh|joke|humor|haha|lol": [
        "Ha! I love your sense of humor! You know what they say — laughter is the best medicine!",
        "Okay that actually made me laugh! You're funny! Tell me another one!",
        "I can already tell we'd have a great time hanging out! 😄",
      ],
      "nervous|shy|awkward|first time|uncomfortable": [
        "Don't be nervous! I promise I don't bite! 😄 Just be yourself.",
        "Honestly, I was a bit nervous too. But you're really easy to talk to!",
        "The hardest part is starting the conversation — and we've already done that! Relax!",
        "Everyone feels awkward sometimes. You're doing great, trust me!",
      ],
      "number|contact|meet again|see you|hang out|plan|date": [
        "I'd really like that! What's the best way to reach you?",
        "Absolutely! Let's exchange numbers. When are you free next?",
        "I've really enjoyed talking to you. Let's definitely do this again!",
        "Yes! How about we grab coffee this weekend? I know a great spot!",
      ],
      "compliment|beautiful|handsome|nice|pretty|attractive|smart|sweet": [
        "Aww, that's so sweet of you! You just made my day! 😊",
        "Thank you! That means a lot coming from you. You're pretty amazing yourself!",
        "Stop, you're making me blush! 😄 But seriously, thank you!",
      ],
    },
    fallback: [
      "I'm really enjoying getting to know you! What else should I know?",
      "You're really interesting to talk to! So what's something surprising about you?",
      "I feel like we could talk for hours! What's your take on this?",
      "Tell me something nobody else knows about you — I promise I'll keep it secret! 😄",
      "You know what I like about this conversation? It feels natural. Don't you think?",
    ],
    closing: "I've had such a great time talking to you! Seriously, this was really fun. Let's make sure we do this again soon. It was wonderful meeting you! Take care! 💫",
  },

  // ===== NEW SCENARIO 19: PARENT-TEACHER MEETING =====
  parentteacher: {
    emoji: "👩‍🏫", title: "Parent-Teacher Meeting", desc: "Discussing child's progress, school issues",
    greeting: "Hello! Thank you for coming in today. I'm Ms. Johnson, your child's English teacher. How are you? Let's discuss how your child is doing in class.",
    keywords: {
      "grade|marks|score|performance|result|report": [
        "Your child scored 78% on the last exam, which is above the class average of 72%.",
        "Overall grades have improved significantly this semester. You should be proud!",
        "There's room for improvement in writing, but reading comprehension is excellent.",
        "I've prepared a detailed progress report. Let me walk you through it.",
      ],
      "behavior|discipline|trouble|fight|bully|disruptive": [
        "I've noticed some behavioral changes recently. Has anything changed at home?",
        "Your child is generally well-behaved, but there have been a few incidents of talking during class.",
        "I want to address this early. Let's work together to find a solution.",
        "Bullying is something we take very seriously. Can you tell me more about what you've noticed?",
      ],
      "homework|assignment|project|work|study|effort": [
        "Homework submission has been consistent — great job supporting that at home!",
        "There have been a few missed assignments. Can we discuss how to improve that?",
        "The quality of work is good, but I'd love to see more effort on the creative writing pieces.",
        "Does your child have a dedicated study space and time at home?",
      ],
      "friend|social|peer|class|group|lonely|shy": [
        "Your child gets along well with classmates. Very sociable and kind.",
        "I've noticed they're a bit quiet in group activities. They might just need some encouragement.",
        "They have a great group of friends here. They're well-liked by peers.",
        "Social skills are just as important as academics. Your child is doing well in both areas.",
      ],
      "strength|good at|talent|excel|best|strong": [
        "Your child excels in reading and has a wonderful vocabulary for their age!",
        "They show great creative thinking — especially in story writing assignments.",
        "Math problem-solving is a real strength. Have you considered extra enrichment?",
        "They're a natural leader in group projects. Other students look up to them.",
      ],
      "weak|struggle|difficult|help|support|improve|concern": [
        "The main area to work on is spelling and grammar. Practice at home would really help.",
        "They struggle a bit with time management during tests. Let's work on that together.",
        "I'd recommend extra reading — 20 minutes a day can make a huge difference.",
        "Would you be open to some after-school tutoring? We have free sessions available.",
      ],
      "activity|sport|club|extracurricular|art|music|drama": [
        "We have debate club, art, music, and sports after school. Has your child shown interest?",
        "Extracurriculars really help with confidence and social skills. I'd encourage participation.",
        "Your child showed great talent in the school play! They should continue with drama.",
      ],
      "next year|plan|future|preparation|advance": [
        "For next year, I'd recommend focusing on writing skills over the summer.",
        "Your child is well-prepared for the next grade level. Keep up the great work!",
        "I'll provide a summer reading list that will help them stay sharp.",
      ],
    },
    fallback: [
      "That's a great question. Let me think about the best way to address that.",
      "Thank you for being so involved. It makes a real difference for your child.",
      "Is there anything else about your child's school experience you'd like to discuss?",
      "Your support at home is crucial. Together, we can help your child succeed.",
    ],
    closing: "Thank you so much for coming in today. Your involvement means the world to your child's education. Let's stay in touch — don't hesitate to email me anytime. Together, we'll make sure your child thrives! 📚",
  },

  // ===== NEW SCENARIO 20: CUSTOMER COMPLAINT =====
  complaint: {
    emoji: "😤", title: "Customer Complaint", desc: "Handling complaints, asking for refunds, escalating",
    greeting: "Hello, thank you for contacting us. I understand you're having an issue. I'm here to help. Could you please tell me what happened?",
    keywords: {
      "product|item|bought|purchased|order|received|broken|defective|damaged": [
        "I'm sorry to hear that. When did you receive the product, and what's wrong with it?",
        "That shouldn't have happened. Do you have your order number handy?",
        "We take product quality very seriously. Let me look into this immediately.",
        "Can you send a photo of the damage? That will help us process your claim faster.",
        "I completely understand your frustration. Let me see what options we have for you.",
      ],
      "refund|money back|return|exchange|compensation|credit": [
        "I can process a full refund for you. Would you prefer it to your original payment method?",
        "We can offer you a replacement, a full refund, or store credit with 15% bonus. What works best?",
        "Your refund will be processed within 3-5 business days. I'm initiating it right now.",
        "I'm also adding a $10 discount code for your next purchase as an apology.",
      ],
      "service|rude|unprofessional|staff|employee|treated|attitude": [
        "I sincerely apologize for that experience. That is not how we train our staff.",
        "Can you tell me when and where this happened so I can address it directly?",
        "Your experience matters to us. I'll personally ensure this is reported to the management.",
        "No customer should ever feel disrespected. I'm so sorry this happened to you.",
      ],
      "wait|waiting|delay|slow|long|time|hours|days|still": [
        "I understand waiting is frustrating. Let me check the status right now.",
        "I can see there was a delay in processing. Let me expedite this for you immediately.",
        "You shouldn't have had to wait this long. I'm escalating this to priority handling.",
        "I'm truly sorry for the delay. Here's what I can do to speed things up...",
      ],
      "manager|supervisor|escalate|higher|authority|someone else": [
        "I completely understand. Let me connect you with my supervisor right away.",
        "Before I transfer you, let me try one more thing — I may be able to resolve this directly.",
        "I'll escalate this to our senior team with a detailed note of your concern.",
        "I want to make sure you're taken care of. My manager will call you back within 2 hours.",
      ],
      "angry|frustrated|upset|disappointed|unacceptable|ridiculous|terrible": [
        "I hear you, and your frustration is completely valid. I want to fix this.",
        "You have every right to be upset. This is not the experience we want for our customers.",
        "I take full responsibility on behalf of our team. Let me make this right.",
        "I understand how disappointing this must be. Here's what I'm going to do for you right now...",
        "I'm not going to make excuses. Let me focus on the solution instead.",
      ],
      "cancel|subscription|membership|stop|end|terminate": [
        "I'm sorry to see you go. Can I ask what prompted this decision?",
        "Before you cancel, can I offer you a discounted rate or a free month?",
        "I've processed the cancellation. You won't be charged again. Is there anything else?",
        "If you change your mind, your account data will be saved for 90 days.",
      ],
      "quality|poor|cheap|worse|expected|advertised|different": [
        "The product should meet our quality standards. Clearly something went wrong here.",
        "Can you describe how it differs from what was advertised? I want to understand fully.",
        "We'll send a replacement that meets your expectations. No additional charge.",
        "Your feedback helps us improve. I'll report this to our quality assurance team.",
      ],
      "loyal|years|customer|always|been|long time": [
        "We truly value your loyalty. Customers like you are the reason we exist.",
        "After all the years you've been with us, you deserve better. Let me make this right.",
        "Your loyalty means everything. I'm adding a VIP bonus to your account as a thank you.",
        "Long-time customers get priority handling. Let me take care of this personally.",
      ],
      "thank|resolved|happy|satisfied|appreciate|helpful": [
        "I'm so glad I could help! Is there anything else you need?",
        "Thank you for your patience. You've been incredibly understanding.",
        "It was my pleasure to assist you. Please don't hesitate to reach out anytime!",
        "I'm happy we could resolve this. Your satisfaction is our top priority!",
      ],
    },
    fallback: [
      "I want to make sure I understand your concern completely. Can you tell me more?",
      "Your experience matters to us. Let me see what I can do.",
      "I'm taking notes on everything you're telling me. We'll address each point.",
      "Please know that I'm on your side here. We want to make this right.",
    ],
    closing: "I'm glad we could resolve this today. Your feedback has been documented and will help us improve. Here's my direct email in case you need anything in the future. Thank you for your patience, and we truly value your business! 😊",
  },
};

// Follow-up questions — asked when user gives short/vague answers
const FOLLOW_UPS = [
  "That's interesting! Can you tell me a bit more about that?",
  "I see! What made you think of that?",
  "Oh really? How did that make you feel?",
  "Interesting! And what happened after that?",
  "I'd love to hear more details. Go on!",
  "That's a great point. Why do you think that is?",
  "Hmm, that's thought-provoking. Can you give me an example?",
  "I appreciate you sharing that. What's your take on it?",
];

// Personality responses — add human touch
const PERSONALITY = {
  empathy: [
    "I totally understand how you feel.",
    "That makes complete sense.",
    "I hear you — that's a really valid point.",
    "I can imagine how that must have felt.",
  ],
  humor: [
    "Ha! You've got a good way of putting things! 😄",
    "I like the way you think! Very creative!",
    "You know what, that's actually a great perspective!",
  ],
  encouragement: [
    "Your English is really good, by the way!",
    "I can tell you've been practicing. Keep it up!",
    "You're expressing yourself really well!",
    "Don't worry about mistakes — that's how we learn!",
  ],
  transitions: [
    "Speaking of which...",
    "That reminds me...",
    "On a related note...",
    "Actually, I was curious about something...",
    "By the way...",
  ],
};

// Main response function — v3 with follow-up logic and personality
export function getSmartResponse(scenarioId, userMessage, messageCount) {
  const scenario = SCENARIOS[scenarioId];
  if (!scenario) return "I'm not sure what to say. Can you try rephrasing?";

  updateContext(userMessage);
  context.exchangeCount = messageCount;

  const lower = userMessage.toLowerCase();
  const words = lower.split(/\s+/);
  const grammarTip = getGrammarTip(userMessage);

  // End conversation after 12+ exchanges
  if (messageCount >= 12) return scenario.closing;

  // Detect very short answers (1-3 words) — ask follow-up
  if (words.length <= 3 && messageCount > 1) {
    // Check if it's a yes/no/ok type response
    if (/^(yes|yeah|yep|sure|ok|okay|no|nope|nah|fine|good|great|thanks|right|true|maybe)$/i.test(lower.trim())) {
      const followUp = pick(FOLLOW_UPS);
      return followUp + grammarTip;
    }
  }

  // Check keywords — with deduplication (don't repeat same topic responses)
  let matchedResponse = null;
  for (const [pattern, responses] of Object.entries(scenario.keywords)) {
    const keywords = pattern.split('|');
    if (keywords.some(kw => lower.includes(kw))) {
      // Skip if we just talked about this exact topic
      if (context.lastKeyword === pattern && responses.length > 1) {
        // Pick a different response than last time
        // eslint-disable-next-line no-loop-func
        const filtered = responses.filter((_, i) => i !== context.lastResponseIndex);
        matchedResponse = pick(filtered.length > 0 ? filtered : responses);
      } else {
        matchedResponse = pick(responses);
      }
      context.lastKeyword = pattern;
      context.lastResponseIndex = responses.indexOf(matchedResponse);

      // Track discussed topics
      if (!context.topics_discussed.includes(pattern.split('|')[0])) {
        context.topics_discussed.push(pattern.split('|')[0]);
      }
      break;
    }
  }

  if (matchedResponse) {
    // Add personality flavor every 3-4 exchanges
    let prefix = "";
    if (messageCount > 0 && messageCount % 3 === 0) {
      prefix = pick(PERSONALITY.empathy) + " ";
    }
    if (messageCount > 0 && messageCount % 5 === 0) {
      prefix = pick(PERSONALITY.encouragement) + " ";
    }

    return prefix + matchedResponse + grammarTip;
  }

  // Smart fallback based on context
  if (context.mood === "negative") {
    return pick([
      "I can hear you're going through a tough time. Would you like to talk about it?",
      "I understand. That must be really challenging. How can I help?",
      "Don't worry — things have a way of working out. Let's figure this out together.",
      "I'm sorry you're dealing with that. Is there anything I can do?",
      "That sounds tough. Remember, it's okay to take things one step at a time.",
    ]) + grammarTip;
  }

  if (context.mood === "positive") {
    return pick([
      "That's great to hear! Your positive energy is contagious!",
      "I love your enthusiasm! Tell me more about that!",
      "Wonderful! It sounds like things are going really well for you!",
      "That's amazing! What's the best part about it?",
      "You seem really happy about that — and you should be!",
    ]) + grammarTip;
  }

  // Topic-aware fallback
  if (context.topic === "work") {
    return pick([
      "Work can be quite a journey, right? What aspect are you thinking about?",
      "The professional world is always changing. What's on your mind about it?",
      "Career matters are always interesting to discuss. Tell me more!",
    ]) + grammarTip;
  }

  if (context.topic === "family") {
    return pick([
      "Family is everything, isn't it? What's happening with yours?",
      "I love hearing about family stories. Please, continue!",
      "Family dynamics are always interesting. What else is going on?",
    ]) + grammarTip;
  }

  if (context.topic === "education") {
    return pick([
      "Learning is a lifelong journey! What are you studying or interested in?",
      "Education opens so many doors. What's your experience been like?",
      "That's great that you value learning! Tell me more about it.",
    ]) + grammarTip;
  }

  // Name-personalized response
  if (context.name && messageCount % 4 === 0) {
    return pick([
      `That's really interesting, ${context.name}! Tell me more.`,
      `I appreciate you sharing that, ${context.name}. What else is on your mind?`,
      `${context.name}, you've got some great thoughts! Keep going!`,
    ]) + grammarTip;
  }

  // Universal topic detection — handles ANY input
  const universalResponse = getUniversalResponse(userMessage);
  if (universalResponse !== null) {
    return universalResponse + grammarTip;
  }

  // Final fallback with transition
  const transition = messageCount > 3 ? pick(PERSONALITY.transitions) + " " : "";
  return transition + pick(scenario.fallback) + grammarTip;
}

// Get all scenarios for the selection screen
export function getAllScenarios() {
  return Object.entries(SCENARIOS).map(([id, s]) => ({
    id, emoji: s.emoji, title: s.title, desc: s.desc
  }));
}

export function resetContext() {
  context = { topic: null, mood: null, name: null, mentioned: [], lastKeyword: null, exchangeCount: 0, topics_discussed: [], lastResponseIndex: -1 };
}

export { SCENARIOS };
